import Intercom, { boot, shutdown } from '@intercom/messenger-js-sdk';
import { API_ROUTES } from '@/api/apiRoutes';
import { log } from '@/logger/logger.ts';
import { safeAxios } from '@/services/safeAxios';
import { sanitizeError } from '@/utils/sanitize-error';
import type { User } from '@/types/auth.types';

const logger = log.getLogger('Intercom');

/**
 * Boots the Messenger for `user`. When `signed`, the backend-issued identity JWT proves the `user_id`
 * to Intercom, and the workspace comes with it so the app always matches the signing secret. If it
 * can't be fetched the Messenger stays off, since enforcement would reject the boot.
 * `getCurrentUserId` is rechecked after the fetch so a sign-out meanwhile doesn't boot the departed user.
 */
export const bootIntercom = async (
  user: User,
  signed: boolean,
  getCurrentUserId: () => string | null,
): Promise<void> => {
  // Unsigned boots only happen with the flag off; remove with FF_INTERCOM_IDENTITY_JWT.
  let appId = 'siifiun4';
  let intercomUserJwt: string | undefined;
  if (signed) {
    try {
      const { data } = await safeAxios.get<{ appId: string; token: string }>(API_ROUTES.users.intercomToken());
      appId = data.appId;
      intercomUserJwt = data.token;
    } catch (error) {
      logger.warn('WEA: Failed to fetch Intercom identity token', sanitizeError(error));
      return;
    }
    if (getCurrentUserId() !== user.uid) return;
  }

  // First boot in the tab loads the widget via `Intercom()`. Calling it again only sends `update`,
  // which can't restart a session after `shutdown`, so the next user is booted with `boot`.
  const start = window.Intercom ? boot : Intercom;
  start({
    app_id: appId,
    user_id: user.uid,
    intercom_user_jwt: intercomUserJwt,
    name: user.displayName || '',
    email: user.email || '',
    created_at: user.createdAt,
    // Weave provides its own help entry point (a custom button in the
    // standalone app, Figma's own help inside Figma), so never show
    // Intercom's floating launcher.
    hide_default_launcher: true,
  });
};

/** Ends the Messenger session so the next user can't see the previous user's conversations. */
export const shutdownIntercom = (): void => shutdown();

export const openIntercom = () => {
  if (!window.Intercom) {
    return logger.error('WEA: Intercom not loaded');
  }

  if (typeof window.Intercom !== 'function') {
    return logger.error('WEA: Intercom is not a function');
  }

  (window.Intercom as (command: string) => void)('show');
};
